/**
 * "Observed at" on chart entries (Phase 16C C7, design §6.7, backend 16B B6).
 *
 * - During a visit it defaults to now; after the visit, to the visit end.
 * - It is limited to the visit window: start − 5 min to end + 5 min (no end yet: now + 1 min).
 * - The form edits a clock time (HH:MM); the date comes from the visit window, so an overnight
 *   visit maps 01:30 to the next day.
 * - MAR uses its administration time as the observed time (no second input).
 */

export interface VisitWindow {
  startedAt?: string | null;
  endedAt?: string | null;
}

export const WINDOW_SLACK_MS = 5 * 60_000;
const FUTURE_SLACK_MS = 60_000;

const pad = (n: number) => String(n).padStart(2, '0');

/** Local HH:MM of an instant. */
export function toClockTime(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function windowBounds(window: VisitWindow, nowMs: number = Date.now()): { min: number | null; max: number } {
  const start = window.startedAt ? Date.parse(window.startedAt) : NaN;
  const end = window.endedAt ? Date.parse(window.endedAt) : NaN;
  return {
    min: Number.isFinite(start) ? start - WINDOW_SLACK_MS : null,
    max: Number.isFinite(end) ? end + WINDOW_SLACK_MS : nowMs + FUTURE_SLACK_MS,
  };
}

/** Default observed instant: now during the visit, the visit end after it. */
export function defaultObservedMs(window: VisitWindow, nowMs: number = Date.now()): number {
  const end = window.endedAt ? Date.parse(window.endedAt) : NaN;
  return Number.isFinite(end) && end < nowMs ? end : nowMs;
}

export function defaultObservedTime(window: VisitWindow, nowMs: number = Date.now()): string {
  return toClockTime(defaultObservedMs(window, nowMs));
}

/**
 * The instant for a clock time: the candidate on the day before / of / after the reference that
 * falls inside the window, else the one closest to the reference (default observed instant).
 */
export function observedIsoFromTime(hhmm: string, window: VisitWindow, nowMs: number = Date.now()): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  if (!m) return null;
  const reference = defaultObservedMs(window, nowMs);
  const { min, max } = windowBounds(window, nowMs);
  const base = new Date(reference);
  const candidates = [-1, 0, 1].map((dayOffset) => {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + dayOffset, Number(m[1]), Number(m[2]), 0, 0);
    return d.getTime();
  });
  const inside = candidates.filter((c) => (min === null || c >= min) && c <= max);
  const pool = inside.length ? inside : candidates;
  const best = pool.reduce((a, b) => (Math.abs(b - reference) < Math.abs(a - reference) ? b : a));
  return new Date(best).toISOString();
}

/** Error text when the instant is outside the visit window, else null. */
export function observedProblem(iso: string | null, window: VisitWindow, nowMs: number = Date.now()): string | null {
  if (!iso) return 'Enter the time the care happened';
  const t = Date.parse(iso);
  const { min, max } = windowBounds(window, nowMs);
  if (min !== null && t < min) return 'Observed time is before the visit started';
  if (t > max) return window.endedAt ? 'Observed time is after the visit ended' : 'Observed time is in the future';
  return null;
}

/** "Late entry — documented 19:40 for care at 15:00" (16B B6: built from the two server fields). */
export function lateEntryLabel(documentedAt?: string | null, observedAt?: string | null): string {
  const fmt = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (documentedAt && observedAt) return `Late entry — documented ${fmt(documentedAt)} for care at ${fmt(observedAt)}`;
  if (documentedAt) return `Late entry — documented ${fmt(documentedAt)}`;
  return 'Late entry';
}
